import { DownloadLink } from "@am-tax/fe-core";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Badge,
    Box,
    Button,
    Card,
    CardBody,
    Flex,
    Heading,
    HStack,
    Icon,
    IconButton,
    Progress,
    Spinner,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
} from "@chakra-ui/react";
import { IconDownload, IconFileImport, IconTemplate, IconTrash } from "@tabler/icons-react";
import { ChangeEvent, Fragment, FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useWageImportJobCreate } from "../../api/hooks/useWageImportJobCreate";
import { useWageImportJobDownloadFileFn } from "../../api/hooks/useWageImportJobDownloadFileFn";
import { useWageImportJobs } from "../../api/hooks/useWageImportJobs";
import { useWageImportJobsDelete } from "../../api/hooks/useWageImportJobsDelete";
import { useWageImportTemplate } from "../../api/hooks/useWageImportTemplate";
import { WageImportJobItem } from "../../api/models/WageImportJob";
import { WageImportsArea } from "../../auth";
import { SetPageTitle } from "../../components/SetPageTitle";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { useAreaPermissions } from "../../hooks";

type IssueItem = {
    RowNumber?: number;
    ColNumber?: number;
    Message?: string;
    RowValue?: string;
};
type IssueSummary = {
    ErrorCount?: number;
    WarningCount?: number;
    Errors?: IssueItem[];
    Warnings?: IssueItem[];
};

const isActiveJob = (job: WageImportJobItem) => job.status === "Pending" || job.status === "InProgress";

const getStatusColorScheme = (status: WageImportJobItem["status"]) => {
    switch (status) {
        case "Pending":
            return "yellow";
        case "InProgress":
            return "blue";
        case "Completed":
            return "green";
        case "Failed":
            return "red";
        case "Cancelled":
            return "gray";
        default:
            return "gray";
    }
};

const getProgressValue = (progress?: number) => {
    if (progress == null) {
        return undefined;
    }

    return progress <= 1 ? progress * 100 : progress;
};

const parseIssueSummary = (issueSummaryJson?: string) => {
    if (!issueSummaryJson) {
        return null;
    }

    try {
        return JSON.parse(issueSummaryJson) as IssueSummary;
    } catch {
        return null;
    }
};

const formatIssueItem = (item: IssueItem) =>
    Object.entries(item)
        .filter(([, value]) => value !== null && value !== undefined && value !== "")
        .map(([key, value]) => `${key.replace(/([A-Z])/g, " $1").trim()}: ${String(value)}`)
        .join(" • ");

const IssueList: FunctionComponent<{ title: string; items: IssueItem[]; color: string }> = ({ title, items, color }) => {
    if (items.length === 0) {
        return null;
    }

    return (
        <VStack align="stretch" spacing={2}>
            <Text fontWeight="semibold" color={color}>
                {title}
            </Text>
            <VStack align="stretch" spacing={1} pl={2}>
                {items.map((item, index) => (
                    <Text key={`${title}-${index}`} fontSize="sm" color="gray.700">
                        • {formatIssueItem(item)}
                    </Text>
                ))}
            </VStack>
        </VStack>
    );
};

export const ClientWageImportsPage: FunctionComponent = () => {
    const { cId } = useParams();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isTemplateDownloading, setIsTemplateDownloading] = useState(false);
    const [jobToDelete, setJobToDelete] = useState<string | null>(null);

    const areaPermissions = useAreaPermissions(WageImportsArea, cId);
    const jobsQuery = useWageImportJobs(cId);
    const createJob = useWageImportJobCreate();
    const deleteJob = useWageImportJobsDelete();
    const downloadJobFile = useWageImportJobDownloadFileFn();
    const downloadTemplate = useWageImportTemplate();
    const { refetch: refetchJobs } = jobsQuery;

    const jobs = useMemo(() => {
        return [...(jobsQuery.data ?? [])].sort((left, right) => Date.parse(right.createdOn) - Date.parse(left.createdOn));
    }, [jobsQuery.data]);

    const activeJob = useMemo(() => jobs.find(isActiveJob), [jobs]);
    const canCreate = areaPermissions.has(WageImportsArea.create);
    const canDelete = areaPermissions.has(WageImportsArea.delete);

    useEffect(() => {
        if (!activeJob) {
            return;
        }

        const intervalId = window.setInterval(() => {
            void refetchJobs();
        }, 5000);

        return () => window.clearInterval(intervalId);
    }, [activeJob, refetchJobs]);

    const onImportClick = () => {
        fileInputRef.current?.click();
    };

    const onImport = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (!file || !cId) {
            event.target.value = "";
            return;
        }

        createJob.mutate(
            {
                tenantId: cId,
                file,
            },
            {
                onSuccess: async () => {
                    await refetchJobs();
                },
                onSettled: () => {
                    event.target.value = "";
                },
            }
        );
    };

    const onDelete = async (jobId: string) => {
        if (!cId) {
            return;
        }

        await deleteJob.mutateAsync({
            tenantId: cId,
            jobId,
        });

        await refetchJobs();
    };

    const onDownloadTemplate = async () => {
        if (!cId) {
            return;
        }

        setIsTemplateDownloading(true);

        try {
            const { data, filename } = await downloadTemplate(cId, () => undefined);
            const blobUrl = window.URL.createObjectURL(data);
            const anchor = document.createElement("a");
            anchor.href = blobUrl;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(blobUrl);
        } finally {
            setIsTemplateDownloading(false);
        }
    };

    if (!cId) {
        return null;
    }

    return (
        <>
            <SetPageTitle title={"Payroll Imports"} />
            <Flex direction="column" gap="1.5rem" width="100%">
                <Card variant="withShadow">
                    <CardBody>
                        <VStack align="stretch" spacing={6}>
                            <Flex justify="space-between" align={{ base: "stretch", md: "center" }} direction={{ base: "column", md: "row" }} gap={4}>
                                <Box>
                                    <Heading size="md" mb={1}>
                                        Payroll Imports
                                    </Heading>
                                    <Text color="gray.500">Upload wage CSV files, monitor processing, and download completed results.</Text>
                                </Box>
                                <HStack spacing={3} alignSelf={{ base: "stretch", md: "auto" }}>
                                    <Button
                                        leftIcon={<Icon as={IconTemplate} />}
                                        variant="outline"
                                        onClick={onDownloadTemplate}
                                        isLoading={isTemplateDownloading}
                                        isDisabled={areaPermissions.loading}
                                    >
                                        Download Template
                                    </Button>
                                    {canCreate && (
                                        <Button
                                            leftIcon={<Icon as={IconFileImport} />}
                                            colorScheme="blue"
                                            onClick={onImportClick}
                                            isLoading={createJob.isLoading}
                                        >
                                            Import CSV
                                        </Button>
                                    )}
                                    <input ref={fileInputRef} type="file" accept=".csv" hidden onChange={onImport} />
                                </HStack>
                            </Flex>

                            {activeJob && (
                                <Box borderWidth="1px" borderColor="blue.100" bg="blue.50" borderRadius="md" p={4}>
                                    <VStack align="stretch" spacing={3}>
                                        <HStack justify="space-between" flexWrap="wrap" spacing={3}>
                                            <HStack spacing={3}>
                                                <Spinner size="sm" color="blue.500" />
                                                <Text fontWeight="medium">{activeJob.statusMessage || "Processing payroll import..."}</Text>
                                            </HStack>
                                            <Badge colorScheme={getStatusColorScheme(activeJob.status)}>{activeJob.status}</Badge>
                                        </HStack>
                                        {activeJob.progress != null && (
                                            <Progress value={getProgressValue(activeJob.progress)} colorScheme="blue" borderRadius="md" />
                                        )}
                                    </VStack>
                                </Box>
                            )}
                        </VStack>
                    </CardBody>
                </Card>

                <Card variant="withShadow">
                    <CardBody>
                        <VStack align="stretch" spacing={4}>
                            <Heading size="md">Job History</Heading>
                            {jobsQuery.isLoading ? (
                                <Flex justify="center" py={8}>
                                    <Spinner />
                                </Flex>
                            ) : jobs.length === 0 ? (
                                <Text color="gray.500">No payroll imports found.</Text>
                            ) : (
                                <TableContainer whiteSpace="unset">
                                    <Table>
                                        <Thead>
                                            <Tr>
                                                <Th>File</Th>
                                                <Th>Status</Th>
                                                <Th>Uploaded On</Th>
                                                <Th>Errors</Th>
                                                <Th>Results</Th>
                                                <Th width="60px"></Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {jobs.map(job => {
                                                const issueSummary = parseIssueSummary(job.issueSummaryJson);
                                                const errorItems = Array.isArray(issueSummary?.Errors) ? issueSummary?.Errors ?? [] : [];
                                                const warningItems = Array.isArray(issueSummary?.Warnings) ? issueSummary?.Warnings ?? [] : [];
                                                const hasIssueDetails = errorItems.length > 0 || warningItems.length > 0;
                                                const showResults = (job.status === "Completed" || job.status === "Failed") && !!job.resultsFileName;

                                                return (
                                                    <Fragment key={job.id}>
                                                        <Tr>
                                                            <Td>
                                                                <DownloadLink
                                                                    downloadFn={async progressCallback => {
                                                                        const { data } = await downloadJobFile(
                                                                            cId,
                                                                            "original",
                                                                            job.id,
                                                                            job.fileName,
                                                                            progressCallback
                                                                        );
                                                                        return { blob: data, fileName: job.fileName };
                                                                    }}
                                                                >
                                                                    <HStack spacing={2} align="center">
                                                                        <Text color="blue.600" title={job.fileName}>
                                                                            {job.fileName}
                                                                        </Text>
                                                                        <Icon as={IconDownload} color="blue.600" />
                                                                    </HStack>
                                                                </DownloadLink>
                                                            </Td>
                                                            <Td>
                                                                <Badge colorScheme={getStatusColorScheme(job.status)}>{job.status}</Badge>
                                                            </Td>
                                                            <Td>{new Date(job.createdOn).toLocaleString()}</Td>
                                                            <Td>
                                                                {job.errorCount > 0 ? (
                                                                    <Text color="red.500" fontWeight="semibold">
                                                                        {job.errorCount}
                                                                    </Text>
                                                                ) : (
                                                                    <Text color="gray.400">—</Text>
                                                                )}
                                                            </Td>
                                                            <Td>
                                                                {showResults ? (
                                                                    <DownloadLink
                                                                        downloadFn={async progressCallback => {
                                                                            const { data, filename } = await downloadJobFile(
                                                                                cId,
                                                                                "results",
                                                                                job.id,
                                                                                job.resultsFileName,
                                                                                progressCallback
                                                                            );
                                                                            return {
                                                                                blob: data,
                                                                                fileName: filename || job.resultsFileName || "wage-import-results.csv",
                                                                            };
                                                                        }}
                                                                    >
                                                                        <HStack spacing={2} align="center">
                                                                            <Text color="blue.600" title={job.resultsFileName}>
                                                                                {job.resultsFileName}
                                                                            </Text>
                                                                            <Icon as={IconDownload} color="blue.600" />
                                                                        </HStack>
                                                                    </DownloadLink>
                                                                ) : (
                                                                    <Text color="gray.400">—</Text>
                                                                )}
                                                            </Td>
                                                            <Td>
                                                                {canDelete ? (
                                                                    <IconButton
                                                                        aria-label="Delete payroll import"
                                                                        variant="ghost"
                                                                        colorScheme="red"
                                                                        icon={<Icon as={IconTrash} />}
                                                                        onClick={() => setJobToDelete(job.id)}
                                                                        isLoading={deleteJob.isLoading}
                                                                        isDisabled={deleteJob.isLoading}
                                                                    />
                                                                ) : (
                                                                    <Text color="gray.400">—</Text>
                                                                )}
                                                            </Td>
                                                        </Tr>
                                                        {hasIssueDetails && (
                                                            <Tr>
                                                                <Td colSpan={6} px={0} py={0} borderBottom="none">
                                                                    <Accordion allowToggle>
                                                                        <AccordionItem border="none">
                                                                            <AccordionButton
                                                                                px={6}
                                                                                py={2}
                                                                                _hover={{ bg: "gray.50" }}
                                                                                color="gray.500"
                                                                                fontSize="sm"
                                                                            >
                                                                                <HStack flex="1" justify="space-between">
                                                                                    <Text>View import details</Text>
                                                                                    <AccordionIcon />
                                                                                </HStack>
                                                                            </AccordionButton>
                                                                            <AccordionPanel px={6} pb={4}>
                                                                                <VStack align="stretch" spacing={4}>
                                                                                    <IssueList title="Errors" items={errorItems} color="red.500" />
                                                                                    <IssueList title="Warnings" items={warningItems} color="gray.600" />
                                                                                </VStack>
                                                                            </AccordionPanel>
                                                                        </AccordionItem>
                                                                    </Accordion>
                                                                </Td>
                                                            </Tr>
                                                        )}
                                                    </Fragment>
                                                );
                                            })}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}
                        </VStack>
                    </CardBody>
                </Card>
            </Flex>
            <ConfirmationDialog
                isOpen={jobToDelete !== null}
                onClose={() => setJobToDelete(null)}
                onConfirm={() => {
                    if (jobToDelete) void onDelete(jobToDelete);
                    setJobToDelete(null);
                }}
                title="Delete Payroll Import"
                confirmLabel="Delete"
                confirmColorScheme="red"
                isDisabled={deleteJob.isLoading}
            >
                Deleting this import will permanently remove all associated candidate payroll data. This action cannot be undone.
            </ConfirmationDialog>
        </>
    );
};
